import React from "react";

// reactstrap components
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import NumberFormat from "react-number-format";

const RANK_CONFIG = {
  1: {
    label: "1st",
    cardGradient: "linear-gradient(180deg, #ffe08a 0%, #f2a63d 45%, #7a4a12 100%)",
    accentColor: "#ffe9b3",
    ringColor: "#ffd76b",
    initialsColor: "#8a5a10",
  },
  2: {
    label: "2nd",
    cardGradient: "linear-gradient(180deg, #9fc7f7 0%, #4f7fdb 45%, #1c2f5e 100%)",
    accentColor: "#f1f5fb",
    ringColor: "#7fb0f5",
    initialsColor: "#1c3d7a",
  },
  3: {
    label: "3rd",
    cardGradient: "linear-gradient(180deg, #f5b787 0%, #d17a3f 45%, #5c2f14 100%)",
    accentColor: "#ffdcb8",
    ringColor: "#e69a5c",
    initialsColor: "#7a3d13",
  },
};

const GEM_COLOR = "#2bffc6";

// Same colors as the tier badges in the member table below, so a member's
// tier reads consistently whether they're on the podium or in the list.
const TIER_META = {
  Platinum: { stars: 5, color: "#8965e0" },
  Gold: { stars: 4, color: "#fb6340" },
  Silver: { stars: 3, color: "#11cdef" },
  Bronze: { stars: 2, color: "#f5365c" },
};
const DEFAULT_TIER_META = { stars: 0, color: "#8898aa" };

const RANK_ICON = {
  1: { icon: "fa-solid fa-crown", color: "#ffd76b", size: 26 },
  2: { icon: "fa-solid fa-medal", color: "#d7dee8", size: 22 },
  3: { icon: "fa-solid fa-medal", color: "#cd7f32", size: 22 },
};

const getInitials = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const RankBadge = ({ rank, accentColor, size = 96 }) => {
  const coreSize = size * 0.56;

  return (
    <div className="loyalty-rank-badge mx-auto" style={{ width: size, height: size }}>
      <div
        className="loyalty-rank-badge-core"
        style={{
          width: coreSize,
          height: coreSize,
          fontSize: size * 0.22,
          boxShadow: `0 0 0 2px ${accentColor}55 inset, 0 .4rem 0.9rem rgba(0,0,0,.4)`,
        }}
      >
        {rank}
      </div>
    </div>
  );
};

const STAR_COLOR = "#ffd600";

const StarRating = ({ count }) => (
  <div className="d-flex justify-content-center mb-2" style={{ gap: 3 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <i
        key={i}
        className={i < count ? "fa-solid fa-star" : "fa-regular fa-star"}
        style={{
          color: i < count ? STAR_COLOR : "rgba(255,255,255,.4)",
          fontSize: 13,
          filter: i < count ? `drop-shadow(0 0 4px ${STAR_COLOR}aa)` : "none",
        }}
      />
    ))}
  </div>
);

const PodiumCard = ({ member, rank }) => {
  if (!member) return null;
  const config = RANK_CONFIG[rank];
  const rankIcon = RANK_ICON[rank];
  const isTop = rank === 1;
  const avatarSize = isTop ? 96 : 76;
  const badgeSize = rankIcon.size + 20;
  const tierMeta = TIER_META[member.Tier] || DEFAULT_TIER_META;

  return (
    <Col lg="4" md="6" className={isTop ? "mb-4 mb-lg-0" : "mb-4 mb-lg-0 mt-lg-6"}>
      <div className="position-relative">
        <div
          className="loyalty-icon-badge-wrap"
          style={{ top: -(badgeSize / 2) - 4 }}
        >
          <div
            className="loyalty-icon-badge d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: badgeSize,
              height: badgeSize,
              background: "#171233",
              boxShadow: `0 0 0 3px ${rankIcon.color}, 0 .5rem 1.1rem rgba(0,0,0,.5)`,
            }}
          >
            <i className={rankIcon.icon} style={{ color: rankIcon.color, fontSize: rankIcon.size }} />
          </div>
        </div>
        <Card
          className="border-0 loyalty-podium-card"
          style={{
            background: config.cardGradient,
            borderRadius: "1.25rem",
            boxShadow: `0 1rem 2.5rem rgba(0,0,0,.45), 0 0 0 1px rgba(255,255,255,.08) inset`,
          }}
        >
          <span className="loyalty-card-shine" style={{ animationDelay: `${(1 + rank) * 0.7}s` }} />
          <CardBody className="text-center py-4 px-3">
            <RankBadge rank={config.label} accentColor={config.accentColor} size={isTop ? 108 : 88} />
            <div
              className="rounded-circle bg-white d-flex align-items-center justify-content-center mx-auto mt-2 mb-3"
              style={{
                width: avatarSize,
                height: avatarSize,
                boxShadow: `0 0 0 4px ${config.ringColor}, 0 .5rem 1.25rem rgba(0,0,0,.35)`,
              }}
            >
              <span
                className="font-weight-bold"
                style={{ fontSize: isTop ? 30 : 22, color: config.initialsColor }}
              >
                {getInitials(member.Name)}
              </span>
            </div>
            <h4 className="text-white mb-2 text-truncate" style={{ textShadow: "0 1px 6px rgba(0,0,0,.35)" }}>
              {member.Name}
            </h4>
            <span
              className="d-inline-block font-weight-bold text-white text-uppercase mb-2 px-3 py-1"
              style={{ fontSize: 11, letterSpacing: ".5px", background: tierMeta.color, borderRadius: "50rem" }}
            >
              {member.Tier}
            </span>
            <StarRating count={tierMeta.stars} />
            <span
              className="d-inline-flex align-items-center font-weight-bold text-white px-3 py-1 mt-2"
              style={{
                fontSize: isTop ? 20 : 17,
                background: "rgba(0,0,0,.25)",
                borderRadius: "50rem",
              }}
            >
              <i className="fa-solid fa-gem mr-2" style={{ color: GEM_COLOR }} />
              <NumberFormat value={member.Points} displayType="text" thousandSeparator />
            </span>
          </CardBody>
        </Card>
      </div>
    </Col>
  );
};

const LoyaltyHeader = ({ topLoyaltyMembers = [] }) => {
  const [first, second, third] = topLoyaltyMembers;

  return (
    <div
      className="header pb-8 pt-5 pt-md-8"
      style={{
        background:
          "radial-gradient(circle at 50% 0%, #33266b 0%, #1a1440 45%, #0a0818 100%)",
      }}
    >
      <style>{`
        .loyalty-rank-badge {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .loyalty-podium-card {
          position: relative;
          overflow: hidden;
        }
        .loyalty-card-shine {
          position: absolute;
          top: -60%;
          left: -60%;
          width: 45%;
          height: 220%;
          opacity: 0.5;
          background: linear-gradient(120deg, transparent 35%, rgba(255,255,255,.6) 50%, transparent 100%);
          transform: rotate(20deg);
          animation: loyalty-shine-sweep 6.5s ease-in-out infinite;
          pointer-events: none;
        }
        .loyalty-rank-badge-core {
          position: relative;
          z-index: 2;
          border-radius: 50%;
          background: rgba(10,8,24,.75);
          color: #fff;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 0 2px rgba(255,255,255,.25) inset, 0 .4rem 0.9rem rgba(0,0,0,.4);
        }
        .loyalty-icon-badge-wrap {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          z-index: 3;
        }
        .loyalty-icon-badge {
          animation: loyalty-float 2.4s ease-in-out infinite;
        }
        @keyframes loyalty-shine-sweep {
          0% { left: -60%; }
          40%, 100% { left: 130%; }
        }
        @keyframes loyalty-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
      <Container fluid>
        <div className="header-body">
          <Row className="justify-content-center mb-4">
            <Col className="text-center" lg="8">
              <h1 className="text-white">Peringkat Loyalitas</h1>
              <p className="text-white-50 mb-4">
                Member dengan poin loyalitas tertinggi bulan ini
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center align-items-end">
            <PodiumCard member={second} rank={2} />
            <PodiumCard member={first} rank={1} />
            <PodiumCard member={third} rank={3} />
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default LoyaltyHeader;
